import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, TimerReset } from "lucide-react";

/**
 * ClipHistoryDropdown — iter-219
 *
 * Overlay that lists the most recent clip events (peak ≥ 0.95) so a
 * user reviewing a mix can see which channels overdrove and at WHAT
 * playhead position they did it. Click any row → set the playhead
 * to that position so the user can scrub directly to the offending
 * moment.
 *
 * Props
 *   open        — boolean
 *   onClose     — () => void
 *   history     — [{ ch:number, whenMs:number, at:number }]   (oldest→newest)
 *   channelNames— [string]
 *   onJumpTo    — (atSeconds) => void
 *   onClear     — () => void
 */
export default function ClipHistoryDropdown({
  open, onClose, history = [], channelNames = [], onJumpTo, onClear,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    };
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    // pointerdown captures the click BEFORE buttons reroute it.
    window.addEventListener("pointerdown", onClick, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onClick, true);
    };
  }, [open, onClose]);

  const fmtTime = (sec) => {
    if (sec == null || !isFinite(sec)) return "—";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };
  // Newest first so the most-relevant entry is at the top.
  const rows = [...history].reverse();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="clip-history-overlay"
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.15 }}
          ref={panelRef}
          data-testid="clip-history-dropdown"
          className="absolute right-4 top-[calc(100%+8px)] z-[80] w-[min(380px,90vw)] overflow-hidden rounded-2xl border border-rose-400/30 bg-[#0c0d18]/95 shadow-[0_24px_60px_-12px_rgba(244,63,94,0.45)] backdrop-blur-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] bg-gradient-to-r from-rose-500/15 via-amber-500/8 to-transparent px-4 py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-300" />
              <div>
                <h3 className="font-display text-sm font-semibold text-white">Clip history</h3>
                <p className="font-mono text-[9px] uppercase tracking-wider text-rose-200/70">
                  {rows.length === 0 ? "No clips recorded" : `${rows.length} event${rows.length === 1 ? "" : "s"} · newest first`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {rows.length > 0 && (
                <button
                  type="button"
                  data-testid="clip-history-clear"
                  onClick={onClear}
                  title="Clear history"
                  className="grid h-7 w-7 place-items-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white"
                  aria-label="Clear clip history"
                >
                  <TimerReset className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                data-testid="clip-history-close"
                onClick={onClose}
                className="grid h-7 w-7 place-items-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white"
                aria-label="Close clip history"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {/* Body */}
          <div className="max-h-[280px] overflow-y-auto p-2">
            {rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-white/55">
                No clips so far. Press play with audio loaded — any channel peaking ≥ 0.95 will land here.
              </p>
            ) : (
              <ul className="space-y-1">
                {rows.map((c, i) => (
                  <li key={`clip-${c.whenMs}-${i}`}>
                    <button
                      type="button"
                      data-testid={`clip-history-row-${i}`}
                      onClick={() => { onJumpTo?.(c.at); }}
                      title={`Jump playhead to ${fmtTime(c.at)}`}
                      className="group grid w-full grid-cols-[48px_1fr_60px] items-center gap-3 rounded-lg px-3 py-2 text-left transition hover:bg-rose-500/[0.10]"
                    >
                      <span className="rounded bg-rose-500/25 px-1.5 py-0.5 text-center font-mono text-[10px] font-semibold tabular-nums text-rose-100 ring-1 ring-rose-400/40">
                        CH {String((c.ch ?? 0) + 1).padStart(2, "0")}
                      </span>
                      <span className="truncate text-xs text-white/80">
                        {channelNames[c.ch] || `Channel ${(c.ch ?? 0) + 1}`}
                      </span>
                      <span className="text-right font-mono text-[11px] tabular-nums text-rose-200/80 group-hover:text-rose-100">
                        {fmtTime(c.at)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
